<script setup lang="ts">
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/zod';
import * as zodObject from 'zod';

import {
	SingleSelectList,
	SingleSelectListItem,
	FormControl,
	FormLabel,
	FormMessage,
	FormItem,
	Collapsible,
	CollapsibleContent,
	FormField,
	Button,
	useToast,
	Separator,
	ExpressBadge,
	Drawer,
	DynamicDrawerContent,
} from '@laam/ui/base';
import { PhBell, PhTruck } from '@phosphor-icons/vue';
import type { ProductOption, ProductVariant } from '~/types/Product';
import type { ProductSchema } from '@laam/cms-shared';

import { type CustomFormTypes } from './size-chart/types';
import CustomSizeForm from './size-chart/CustomSizeForm.vue';
import SizeChart from './size-chart/SizeChart.vue';
import ProductVariantSize from '~/components/product-detail-v2/ProductVariantSize.vue';
import SizeChartModal from '~/components/product-detail-v2/size-chart/SizeChartModal.vue';
import { isOriginalProductPrice } from '~/utils/helpers.ts';
import LazyImage from '../LazyImage.vue';
import { DataSourceEnum } from '~/utils/enums';
import { useDeviceType } from '~/utils/use-device-type';

const { deviceType } = useDeviceType();

const QuantityComponent = defineAsyncComponent(
	() => import('./QuantityComponent.vue'),
);
const AddedToBag = defineAsyncComponent(() => import('./AddedToBag.vue'));
import { useRoute } from 'vue-router';
const route = useRoute();
const { $cathodeClient } = useNuxtApp();
const { $eventClient } = useNuxtApp();
const { storeType } = useGlobalStore();

interface ProductFormProps {
	productData: ProductSchema;
	options: ProductOption[];
	variants?: ProductVariant[];
	sizeChartId: number;
	quantity: number;
	fromTest: boolean;
	selectedVariantData: ProductVariant | null;
	parent: string;
	prices: Object;
}
const props = defineProps<ProductFormProps>();

const emit = defineEmits([
	'selectedVariantData',
	'onSuccess',
	'onInvalidSubmit',
	'saveCustomform',
	'closeAddToBagDrawer',
]);
const CartStore = useCartStore();
const customForm = ref<CustomFormTypes>();
const addToCartToast = ref<boolean>(false);
const sizeVal = ref<string>('');
const selectedQuantity = ref(props.quantity);
const CurrencyStore = useCurrency();
const { getFormattedPrice } = CurrencyStore;
const { currencySymbol } = storeToRefs(CurrencyStore);
const { toast } = useToast();
const { pdpRedesignExperiment } = useStatsigExperiment();

const showCustomFormError = ref(false);
const showAddedToBagDrawer = ref(false);
const close = inject<() => void>('close', () => {});

const closeProductFormDrawer = inject('closeProductFormDrawer') as () => void;
const getProductStatus = inject('getProductStatus') as () => string;
const showButton = inject('showButton') as (cta: string) => boolean;
const selectedVariantPrice = inject('selectedVariantPrice', ref<string>(''));
const productDate = inject('productDate', ref<string>(''));
const carouselTitle = inject('carouselTitle') as Ref<string | undefined>;
const isQuickAddPDP = inject('quickAddForm', ref(false));
const headerVisible = inject('headerVisible') as Ref<boolean>;
const remainingDays = inject('remainingDays', ref<number | null>(null));

const eventsMetaData = computed(() => {
	const queryParamsString =
		sessionStorage.getItem('cathodeQueryParamsObject') || '';

	let queryParams: {
		dataSource: string;
		clickedFrom: string[];
		version: string | null;
	} = {
		dataSource: DataSourceEnum.DIRECT,
		clickedFrom: [],
		version: null,
	};

	if (queryParamsString) queryParams = JSON.parse(queryParamsString);

	return queryParams;
});

const notifyMeProduct: Ref<{
	show: boolean;
	shopifyId: number;
	url: string;
	disabled: number[];
}> = inject('notifyMeProduct')!;

const productFormSchema = zodObject.object(
	Object.fromEntries(
		props.options.map((option) => {
			const optionName = option.name.toLowerCase();
			return [
				optionName,
				zodObject
					.string({ required_error: `You need to select ${option.name}.` })
					.refine((value) => option.values.includes(value)),
			];
		}),
	),
);

const form = useForm({
	validationSchema: toTypedSchema(productFormSchema),
	validateOnMount: false,
	keepValuesOnUnmount: false,
});

const closeAddToBagDrawer = () => {
	if (deviceType.value !== 'desktop') {
		showAddedToBagDrawer.value = false;
		close();
		closeProductFormDrawer();
		emit('closeAddToBagDrawer');
	}
};

const openAddToBagDrawer = () => {
	if (deviceType.value !== 'desktop') {
		showAddedToBagDrawer.value = true;
	}
};
const onSubmit = form.handleSubmit(onSuccess, onInvalidSubmit);
const selectedSize = ref<string | null>(null);
const showSizeDetails = ref(false);

const selectedVariant = ref<ProductVariant | null>();
const selectedVariantDataObject = ref<ProductSchema['variants'][number] | null>(
	null,
);
async function onSuccess() {
	emit('onSuccess');
	handleAddToCart();
}

function onInvalidSubmit() {
	emit('onInvalidSubmit');
}

const formattedPrice = computed(() => {
	return props.prices.showHvcDiscount
		? CurrencyStore.getFormattedPrice(props.prices.hvcPrice!, true)
		: CurrencyStore.getFormattedPrice(
				props.prices.price,
				!props.prices.isOriginalPrice,
			);
});

onUnmounted(() => {
	form.resetForm();
	emit('closeAddToBagDrawer');
});

watch(form.values, checkVariant);
async function checkVariant() {
	const optionsArray = Object.values(form.values);

	const sortedSelectedTitle = optionsArray.sort().join(' / ');

	// Check if any variant matches the sorted and joined title
	props.variants!.some((variant) => {
		const sortedVariantTitle = variant.title.split(' / ').sort().join(' / ');
		const exists = sortedVariantTitle === sortedSelectedTitle;

		if (exists) {
			selectedVariant.value = variant;
			selectedVariantDataObject.value = variant;
			emit('selectedVariantData', selectedVariant.value);
		} else selectedVariant.value = null;
	});
}

const testId = ref('');
watch(
	() => CartStore.cartId,
	() => {
		if (CartStore.cartId) {
			testId.value = 'add-to-bag-button';
		}
	},
	{ immediate: true },
);

const showSizeChartButton = computed(() => {
	return !!props.sizeChartId;
});
const sendViewSizeChartEvent = () => {
	$eventClient.sendEvent('view-size-chart', {
		l4Node: props.productData.node_l4,
		l3Node: props.productData.node_l3,
		noOfpieces: props.productData.attributes['number-of-pieces'],
	});
};
const saveCustomform = (value: CustomFormTypes) => {
	customForm.value = value;
	emit('saveCustomform', customForm.value);
};

const handleSizeSelect = (size: string, option: ProductOption) => {
	if (option) {
		$eventClient.sendEvent('selected-variant', {
			variantName: option.name,
			variantValue: size,
		});
	}
	if (selectedSize.value === size && option.name.toLowerCase() === 'size') {
		sizeVal.value = size;
		showSizeDetails.value = !showSizeDetails.value;
	} else if (option.name.toLowerCase() === 'size') {
		sizeVal.value = size;
		selectedSize.value = size;
		showSizeDetails.value = true;
	}
};

const getAddToBagSource = () => {
	if (props.parent === 'quick-view') {
		const page = route.name;
		if (page === 'nodes-handle') {
			return 'nodes';
		} else if (page === 'brands-handle') {
			return 'brands';
		} else if (page === 'products-handle') {
			return carouselTitle;
		} else if (page === 'index') {
			return 'women-homepage';
		} else if (page === 'men') {
			return 'men-homepage';
		} else if (page === 'kids') {
			return 'kids-homepage';
		}
	}
	return 'PDP';
};

const handleNotifyMe = () => {
	notifyMeProduct.value.show = true;
	notifyMeProduct.value.shopifyId = props.productData.shopify_id;
	notifyMeProduct.value.url = props.productData.handle;

	$eventClient.sendEvent('notify-me', {
		meta_data: {
			product: props.productData,
		},
	});
};

const isOriginalVariantPrice = computed(() => {
	if (selectedVariantDataObject.value) {
		return isOriginalProductPrice(
			parseFloat(selectedVariantDataObject.value.compare_at_price || '0'),
			parseFloat(selectedVariantDataObject.value.price),
		);
	}

	return false;
});

const tagsLowerCase = computed(() =>
	props.productData.tags.map((tag) => tag.toLowerCase()),
);
async function handleAddToCart() {
	if (
		selectedVariantDataObject.value &&
		selectedVariantDataObject.value.inventory_quantity > 0
	) {
		const customSizeData = [];
		if (selectedVariantDataObject.value.title === 'Custom Size') {
			if (customForm.value !== undefined) {
				for (let key in customForm.value) {
					const value = customForm.value[key as keyof typeof customForm.value];
					const obj: { key: string; value: string } = {
						key: '',
						value: '',
					};
					if (key.includes('units')) {
						key = 'units';
					}
					obj['key'] = key;
					obj['value'] = value.toString();
					customSizeData.push(obj);
				}
			} else {
				showCustomFormError.value = true;
				setTimeout(() => {
					showCustomFormError.value = false;
				}, 5000);
				return;
			}
		}
		let response;
		try {
			response = await CartStore.handleAddToCart(
				selectedVariantDataObject.value,
				selectedQuantity.value,
				customSizeData,
			);

			if (response.code === 200) {
				if (deviceType.value === 'desktop') {
					addToCartToast.value = !addToCartToast.value;
				}
				openAddToBagDrawer();
				$cathodeClient.queueEvent('add_to_cart', {
					id: String(selectedVariantDataObject.value.shopify_id),
					source: window.location.href,
					sku: String(selectedVariantDataObject.value.shopify_id),
					quantity: 1,
					price: selectedVariantPrice.value,
					currencyCode: 'PKR',
					data_source: `${eventsMetaData.value.dataSource}${eventsMetaData.value.version ? `- ${eventsMetaData.value.version}` : ''}`,
					clicked_from: eventsMetaData.value.clickedFrom.join(','),
					type: props.parent,
				});
				$eventClient.sendEvent('add-to-cart', {
					id: String(selectedVariantDataObject.value.shopify_id),
					product_type: props.parent,
					quantity: selectedQuantity.value,
					shipping_date: productDate.value,
					product_handle: props.productData.handle,
					price: selectedVariantPrice.value,
					meta_data: {
						product: props.productData,
						selectedVariant: selectedVariantDataObject.value!,
					},
					clicked_from: getAddToBagSource() as string,
					content_ids: [String(selectedVariantDataObject.value.shopify_id)],
					contents: [
						{
							id: String(selectedVariantDataObject.value.shopify_id),
							quantity: selectedQuantity.value,
						},
					],
					currency: 'PKR',
					value: parseFloat(selectedVariantPrice.value),
				});

				form.resetForm();
			} else {
				throw response;
			}
		} catch (error) {
			console.log(error);
			const err = error as { code: number; data: { message: string }[] };
			if (err.code! === 800) {
				toast({
					variant: 'error',
					title: err.data[0]!.message,
					duration: 3000,
				});
			}
		}
	}
}

watch(addToCartToast, () => {
	toast({
		description: h('div', { class: 'flex flex-col' }, [
			h(
				'span',
				{ class: 'text-gray-900 text-sm font-semibold' },
				'Item added to bag',
			),
			h(
				'span',
				{ class: 'text-blue-600 text-xs font-medium' },
				`Get it by ${productDate.value} `,
			),
		]),
		variant: 'info',
		class:
			'py-md px-xl items-center justify-center lg:gap-xl bg-white !absolute bottom-[76vh] right-[26vh] w-[300px]',
		duration: 3000,
		img: h(LazyImage, {
			src: props.productData.image.src,
			alt: props.productData.image.alt,
			width: 25,
			class: 'w-full',
		}),
		action: h(
			'button',
			{
				class:
					'py-md px-xl bg-white rounded-small text-gray-800 text-sm font-semibold hover:cursor-pointer border border-gray-300',
				onClick: () => {
					navigateTo('/cart');
				},
			},
			['View Bag'],
		),
	});
});

const scrollToError = () => {
	nextTick(() => {
		const errorElement = document.getElementById('formMessage');
		if (errorElement) {
			errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}
	});
};

watch(form.errors, () => {
	scrollToError();
});
</script>

<template>
	<div
		class="flex w-full flex-col overflow-auto"
		:class="{
			'!p-0 ': deviceType !== 'desktop',
		}"
	>
		<div v-if="!showAddedToBagDrawer" class="flex flex-col w-full">
			<div
				v-if="deviceType !== 'desktop' && !pdpRedesignExperiment?.showSizeForm"
				class="flex gap-xs items-baseline pb-3xl"
			>
				<span class="text-sm font-normal text-gray-700"> From </span>
				<span class="text-error-600 text-lg font-semibold">
					{{ currencySymbol }} {{ formattedPrice }}
				</span>
			</div>
			<Separator
				v-if="deviceType !== 'desktop' && !pdpRedesignExperiment?.showSizeForm"
				class="h-[1px] bg-gray-200 w-full"
			/>
			<form
				:id="isQuickAddPDP ? 'quickAddForm' : 'productForm'"
				class="pdp__form flex flex-col"
				:class="{
					'max-h-[calc(100vh-100px)] overflow-y-auto pt-3xl pb-[38px]':
						deviceType !== 'desktop' && !pdpRedesignExperiment?.showSizeForm,
					'!pb-[26px]':
						getProductStatus() === 'out-of-stock' &&
						deviceType !== 'desktop' &&
						!pdpRedesignExperiment?.showSizeForm,
					'!px-3xl':
						pdpRedesignExperiment?.showSizeForm && deviceType !== 'desktop',
				}"
				@submit="onSubmit"
			>
				<div
					v-for="(option, index) in props.options"
					:key="index"
					data-testid="product-variant-group"
				>
					<FormField
						:key="option.name.toLowerCase() + index"
						v-slot="{ componentField }"
						:class="'product_form' + option.name.toLowerCase()"
						:name="option.name.toLowerCase()"
						type="radio"
					>
						<FormItem class="pdp--form-item lg:gap-md p-0 flex flex-col">
							<div class="flex items-center w-full justify-between">
								<div
									class="flex lg:flex-row w-full lg:items-center items-start justify-between"
								>
									<FormLabel
										class="lg:text-md text-sm font-semibold text-gray-500 w-full"
										:class="
											'pdp__form-options-label-' + option.name.toLowerCase()
										"
										:for="option.name.toLowerCase()"
									>
										<div class="flex">
											<span v-if="option.name.toLowerCase() === 'size'">{{ option.name }}: {{ sizeVal }}</span>
											<span v-else>{{ option.name }}:</span>
										</div>
									</FormLabel>
									<div
										v-if="
											option.name.toLowerCase().includes('size') &&
											showSizeChartButton
										"
										class="pdp__size-chart flex !justify-end w-full"
										@click="sendViewSizeChartEvent"
									>
										<SizeChart v-if="isMobileView()" />
										<SizeChartModal v-else />
									</div>
									<p
										v-if="showCustomFormError"
										class="text-error-500 text-sm font-medium"
									>
										Please fill in size form
									</p>
								</div>
							</div>

							<FormControl class="">
								<SingleSelectList
									v-bind="componentField"
									class="gap-md flex flex-row flex-wrap !mt-md !mb-3xl"
								>
									<SingleSelectListItem
										v-for="size in option.values"
										:key="size"
										class="pdp__form-options-button relative"
										:value="size"
										data-testid="variant-group-option"
										@click="handleSizeSelect(size, option)"
									>
										<div class="flex items-center gap-md justify-center">
											<ClientOnly v-if="size === 'Custom Size'">
												<CustomSizeForm
													:option="option"
													@save-data="saveCustomform"
												/>
												<template #fallback> Custom Size </template>
											</ClientOnly>

											<ProductVariantSize
												v-else
												class="pdp_product-variant-size"
												:option="option.name"
												:title="size"
												:from-test="props.fromTest"
											/>
										</div>
									</SingleSelectListItem>
								</SingleSelectList>
							</FormControl>
							<FormMessage id="formMessage" class="!mt-0 !pt-0" />
						</FormItem>
					</FormField>

					<div
						v-if="
							getProductStatus() === 'out-of-stock' &&
							pdpRedesignExperiment?.showSizeForm &&
							deviceType !== 'desktop' &&
							selectedSize
						"
						class="gap-xs flex items-center mb-md"
					>
						<div class="border-red-100 border-[3px] rounded-full">
							<div class="size-md bg-red-600 rounded-full" />
						</div>

						<span class="font-medium text-error-600 text-sm">Currently out of stock</span>
					</div>
					<Collapsible
						v-if="
							option.name.toLowerCase().includes('size') &&
							selectedSize &&
							showSizeDetails
						"
						v-model:open="showSizeDetails"
						class="rounded-md"
					>
						<CollapsibleContent class="p-4">
							<SizeChartModal :size="selectedSize" />
							<SizeChart v-if="isMobileView()" :size="selectedSize" />
						</CollapsibleContent>
					</Collapsible>

					<Separator
						v-if="
							option.name.toLowerCase() === 'size' &&
							deviceType !== 'desktop' &&
							!pdpRedesignExperiment?.showSizeForm
						"
						class="h-[1px] bg-gray-200 mb-3xl"
					/>
				</div>
			</form>

			<div
				v-if="deviceType !== 'desktop' && !pdpRedesignExperiment?.showSizeForm"
				:data-parent="props.parent"
				:data-store-type="storeType"
				:data-header-visible="headerVisible"
				class="pdp-cta lg:static z-10 fixed left-0 right-0 bottom-[77px] transition-all px-3xl mt-3xl"
			>
				<div
					v-if="getProductStatus() === 'out-of-stock'"
					class="pdp__cta-notify lg:hidden px-3xl py-xs border-y gap-md flex items-center justify-center w-full text-xs bg-error-50 border-error-200 border rounded-t-xs"
				>
					<div class="size-md bg-red-600 rounded-full" />
					<span class="font-semibold text-error-600">Currently out of stock</span>
				</div>

				<div
					v-else
					class="pdp__shipping-estimated-shipping-badge lg:hidden px-xl py-sm border-y gap-xs flex justify-between w-full text-xs bg-blue-50 border-blue-200 border rounded-t-xs"
					:class="{
						'bg-gray-50 border-gray-200':
							remainingDays! > 2 &&
							!tagsLowerCase.includes('deliverydays_readytoship_rdlrg'),
					}"
				>
					<div
						v-if="
							remainingDays! < 2 &&
							tagsLowerCase.includes('deliverydays_readytoship_rdlrg')
						"
						class="flex gap-xxs items-center"
					>
						<ExpressBadge />
						<span class="text-gray-900 italic font-bold text-xs">
							shipping
						</span>
					</div>
					<div v-else class="flex gap-md items-center">
						<PhTruck :size="18" :color="'#141414'" />
						<span class="lg:text-sm font-medium text-gray-900 text-xs">
							Standard Shipping
						</span>
					</div>

					<div class="flex items-center gap-xs">
						<span class="text-gray-900 text-xs font-normal">Ship By </span>
						<span class="font-semibold text-gray-900">{{ productDate }}</span>
					</div>
				</div>
			</div>

			<separator
				v-if="deviceType !== 'desktop' && !pdpRedesignExperiment?.showSizeForm"
				class="bg-gray-200 h-[1px] fixed bottom-[77px] -ml-3xl"
			/>

			<div
				v-if="deviceType === 'desktop' || !pdpRedesignExperiment?.showSizeForm"
				class="flex flex-col w-full gap-sm"
				:class="{
					'sticky bottom-0 flex !flex-row items-center justify-start pt-xl gap-3xl bg-white right-0 border-t border-gray-200 footer':
						deviceType !== 'desktop',
				}"
			>
				<div
					class="flex w-full bg-white lg:flex-col lg:items-start items-center justify-between lg:gap-3xl"
				>
					<div
						v-if="getProductStatus() === 'out-of-stock' && !isMobileView()"
						class="info-out-of-stock flex justify-center items-center lg:w-full py-md px-12 rounded-medium bg-error-50 border border-error-200 gap-sm"
					>
						<div class="size-md bg-red-600 rounded-full" />
						<span class="text-xs text-error-600 font-semibold">Currently out of stock</span>
					</div>

					<QuantityComponent
						v-if="getProductStatus() !== 'out-of-stock'"
						class="p-none"
						:product-data="props.productData"
						:selected-variant-data="selectedVariantData"
						:stock-status="getProductStatus()"
						@selected-quantity="
							(quantity: number) => (selectedQuantity = quantity)
						"
					/>
					<div
						class="lg:p-none flex bg-white lg:flex-row flex-row-reverse w-full"
					>
						<Button
							v-if="
								showButton('add-to-cart') &&
								!CartStore.showViewBag &&
								getProductStatus() !== 'out-of-stock'
							"
							variant="primary"
							size="lg"
							:form="isQuickAddPDP ? 'quickAddForm' : 'productForm'"
							class="w-full h-10xl"
							:data-testId="testId"
							:class="{
								'py-xl px-4xl bg-gray-900 gap-sm w-full addToBagButton':
									deviceType !== 'desktop',
							}"
						>
							<ButtonLoader v-if="CartStore.loading" />
							<span v-else class="lg:text-md text-sm addToBagText">Add to bag</span>
							<span
								v-if="selectedVariantPrice"
								class="lg:text-md text-sm addToBagText"
							>
								{{
									`(${currencySymbol} ${getFormattedPrice(parseFloat(selectedVariantPrice) * selectedQuantity, !isOriginalVariantPrice)})`
								}}
							</span>
						</Button>

						<Button
							v-else-if="
								showButton('add-to-cart') && CartStore.showViewBag === true
							"
							variant="secondary"
							size="lg"
							:form="isQuickAddPDP ? 'quickAddForm' : 'productForm'"
							class="w-full"
							:class="{
								'py-xl px-4xl bg-gray-900 gap-sm w-[218px]':
									deviceType !== 'desktop',
							}"
							@click="
								async () => {
									props.parent === 'quick-view' && close();
									await navigateTo('/cart');
								}
							"
						>
							<PhHandbag />
							<p>View Bag</p>
						</Button>
						<Button
							v-else-if="
								showButton('notify-me') || getProductStatus() === 'out-of-stock'
							"
							variant="primary"
							size="lg"
							class="w-full"
							:class="{
								'py-xl px-4xl bg-gray-900 gap-sm': deviceType !== 'desktop',
							}"
							:disabled="
								notifyMeProduct.disabled.includes(productData.shopify_id)
							"
							@click="handleNotifyMe"
						>
							<PhBell :size="24" />
							<p>Notify Me</p>
						</Button>
					</div>
				</div>
			</div>
		</div>

		<div v-else class="flex w-full transition-all duration-300 ease-in">
			<Drawer v-model:open="showAddedToBagDrawer">
				<DynamicDrawerContent :height="'50vh'" :max-height="80">
					<AddedToBag
						:drop-handle="props.productData.drop_handle"
						:shopify-id="props.productData?.shopify_id"
						@close-drawer="closeAddToBagDrawer"
					/>
				</DynamicDrawerContent>
			</Drawer>
		</div>
	</div>
</template>

<style scoped>
.pdp__form-options-button {
	@apply px-xl  py-md w-fit rounded-xs border-[1.75px] border-solid border-gray-300 text-gray-800 bg-white data-[state=checked]:border-gray-900 data-[state=checked]:bg-white text-sm font-medium min-w-10xl h-9xl;
}

.pdp__form-options-button[data-disabled='true'] {
	@apply text-gray-400 border-gray-200 cursor-not-allowed;
}
.pdp-btn-add-to-bag {
	@apply py-xl px-4xl gap-sm bg-gray-800;
}

@media (max-width: 380px) {
	.addToBagButton {
		height: 40px;
		@apply px-lg;
	}
	.addToBagText {
		@apply text-xs;
	}
	.footer {
		@apply pt-5xl;
	}
}
</style>
